/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import { SOC2Control, SOC2Category, SOC2ControlMapping, ComplianceSecurityBalance, SOC2ControlEffectiveness, SOC2Scenario } from '../types/soc2';
import { SOC2_CONTROLS } from '../constants/soc2Controls';

export class SOC2ControlManager {
  private controls: Map<string, SOC2Control>;
  private mappings: Map<string, SOC2ControlMapping>;
  private scenarios: Map<string, SOC2Scenario>;
  private effectivenessData: Map<string, SOC2ControlEffectiveness>;

  constructor() {
    this.controls = new Map();
    this.mappings = new Map();
    this.scenarios = new Map();
    this.effectivenessData = new Map();
    this.initializeControls();
  }

  private initializeControls(): void {
    SOC2_CONTROLS.forEach(control => {
      this.controls.set(control.id, control);
      this.mappings.set(control.id, this.createInitialMapping(control));
    });
  }

  // Control Management
  public getControl(controlId: string): SOC2Control | undefined {
    return this.controls.get(controlId);
  }

  public getAllControls(): SOC2Control[] {
    return Array.from(this.controls.values());
  }

  public getControlsByCategory(category: SOC2Category): SOC2Control[] {
    return Array.from(this.controls.values()).filter(control => control.category === category);
  }

  public updateControlEffectiveness(controlId: string, effectiveness: Partial<SOC2ControlEffectiveness>): void {
    const control = this.controls.get(controlId);
    if (control) {
      control.effectiveness = effectiveness.operatingEffectiveness || control.effectiveness;
      control.maturityLevel = this.calculateMaturityLevel(effectiveness);
    }

    const existingData = this.effectivenessData.get(controlId) || this.createInitialEffectivenessData(controlId);
    this.effectivenessData.set(controlId, { ...existingData, ...effectiveness });
  }

  // Implementation Management
  public implementControl(controlId: string, resources: string[], budget: number): ImplementationResult {
    const control = this.controls.get(controlId);
    if (!control) {
      return { success: false, reason: 'Control not found' };
    }

    if (control.implementation.status === 'implemented' || control.implementation.status === 'operating_effectively') {
      return { success: false, reason: 'Control already implemented' };
    }

    const cost = control.implementationCost;
    if (budget < cost) {
      return { success: false, reason: 'Insufficient budget' };
    }

    // Update implementation status
    control.implementation.status = 'implemented';
    control.implementation.lastUpdated = Date.now();
    control.implementation.implementedBy = resources;
    control.implementation.status = 'implemented';

    // Set initial effectiveness
    const initialEffectiveness = this.calculateInitialEffectiveness(control);
    this.updateControlEffectiveness(controlId, initialEffectiveness);

    return {
      success: true,
      cost,
      timeline: control.implementation.timeline.estimatedDuration,
      expectedRiskReduction: control.riskReduction,
      businessImpact: control.businessImpact
    };
  }

  public testControl(controlId: string): TestingResult {
    const control = this.controls.get(controlId);
    if (!control) {
      return { success: false, reason: 'Control not found' };
    }

    if (control.implementation.status !== 'implemented') {
      return { success: false, reason: 'Control not implemented' };
    }

    // Simulate testing process
    const testResult = this.simulateControlTesting(control);

    // Update effectiveness based on test results
    const effectiveness = this.calculateEffectivenessFromTests(testResult);
    this.updateControlEffectiveness(controlId, effectiveness);

    return {
      success: testResult.passed,
      testResult,
      effectiveness,
      recommendations: this.generateTestRecommendations(testResult)
    };
  }

  // Compliance vs Security Balance Analysis
  public analyzeComplianceSecurityBalance(): ComplianceSecurityBalance {
    const allControls = this.getAllControls();
    const securityControls = allControls.filter(c => c.category === 'security');
    const otherControls = allControls.filter(c => c.category !== 'security');

    const complianceScore = this.calculateComplianceScore(otherControls);
    const securityScore = this.calculateSecurityScore(securityControls);
    const balanceRatio = this.calculateBalanceRatio(complianceScore, securityScore);
    const businessImpact = this.calculateBusinessImpact(allControls);
    const riskScore = this.calculateOverallRiskScore(allControls);

    return {
      complianceScore,
      securityScore,
      balanceRatio,
      businessImpact,
      riskScore,
      recommendations: this.generateBalanceRecommendations(balanceRatio, complianceScore, securityScore),
      optimizationOpportunities: this.identifyOptimizationOpportunities(allControls)
    };
  }

  public optimizeComplianceSecurityBalance(targetRatio: number): OptimizationPlan {
    const currentBalance = this.analyzeComplianceSecurityBalance();
    const controls = this.getAllControls();

    const optimizations = this.identifyOptimizations(controls, currentBalance, targetRatio);
    const totalCost = optimizations.reduce((sum, opt) => sum + opt.cost, 0);
    const expectedBenefit = this.calculateExpectedBenefit(optimizations);
    const implementationPlan = this.createImplementationPlan(optimizations);

    return {
      currentBalance: currentBalance.balanceRatio,
      targetBalance: targetRatio,
      optimizations,
      totalCost,
      expectedBenefit,
      implementationPlan,
      riskMitigation: this.calculateRiskMitigation(optimizations),
      timeline: Math.max(...optimizations.map(opt => opt.timeframe))
    };
  }

  // Scenario Management
  public createScenario(config: ScenarioConfig): SOC2Scenario {
    const scenario: SOC2Scenario = {
      id: this.generateId(),
      title: config.title,
      description: config.description,
      category: config.category,
      difficulty: config.difficulty,
      businessContext: config.businessContext,
      threatLandscape: config.threatLandscape,
      availableControls: config.availableControls,
      targetControls: config.targetControls,
      constraints: config.constraints,
      objectives: config.objectives,
      successMetrics: config.successMetrics,
      timeLimit: config.timeLimit,
      costBudget: config.costBudget,
      riskTolerance: config.riskTolerance
    };

    this.scenarios.set(scenario.id, scenario);
    return scenario;
  }

  public executeScenario(scenarioId: string, playerDecisions: PlayerDecision[]): ScenarioResult {
    const scenario = this.scenarios.get(scenarioId);
    if (!scenario) {
      return { success: false, reason: 'Scenario not found' };
    }

    const evaluation = this.evaluateScenarioPerformance(scenario, playerDecisions);
    const learningOutcomes = this.generateLearningOutcomes(evaluation);
    const recommendations = this.generatePlayerRecommendations(evaluation);

    return {
      success: true,
      scenarioId,
      score: evaluation.score,
      objectives: evaluation.objectives,
      securityComplianceBalance: evaluation.balance,
      learningOutcomes,
      recommendations,
      timeUsed: evaluation.timeUsed,
      budgetUsed: evaluation.budgetUsed
    };
  }

  // Analytics and Insights
  public generateControlInsights(): ControlInsights {
    const controls = this.getAllControls();
    const effectiveness = Array.from(this.effectivenessData.values());

    return {
      overallMaturity: this.calculateOverallMaturity(controls),
      categoryBreakdown: this.generateCategoryBreakdown(controls),
      criticalControls: this.identifyCriticalControls(controls),
      riskHeatmap: this.generateRiskHeatmap(controls),
      improvementOpportunities: this.identifyImprovementOpportunities(effectiveness),
      costOptimization: this.analyzeCostOptimization(controls),
      complianceTrends: this.analyzeComplianceTrends(effectiveness)
    };
  }

  public predictAuditReadiness(timeHorizon: number): AuditReadinessPrediction {
    const controls = this.getAllControls();
    const currentReadiness = this.calculateAuditReadiness(controls);
    const projectedReadiness = this.projectAuditReadiness(controls, timeHorizon);
    const gaps = this.identifyAuditGaps(controls);
    const remediationPlan = this.createRemediationPlan(gaps, timeHorizon);

    return {
      currentReadiness,
      projectedReadiness,
      gaps,
      remediationPlan,
      confidence: this.calculatePredictionConfidence(controls),
      recommendations: this.generateAuditRecommendations(gaps)
    };
  }

  // Private Helper Methods
  private createInitialMapping(control: SOC2Control): SOC2ControlMapping {
    return {
      controlId: control.id,
      trustServices: [],
      implementationLevel: control.implementation.status === 'implemented' ? 50 : 0,
      testResults: [],
      auditEvidence: [],
      gaps: [],
      remediationPlan: null,
      businessValue: this.calculateBusinessValue(control)
    };
  }

  private createInitialEffectivenessData(controlId: string): SOC2ControlEffectiveness {
    const control = this.controls.get(controlId);
    return {
      controlId,
      designEffectiveness: 0,
      operatingEffectiveness: 0,
      testCoverage: 0,
      exceptionRate: 0,
      meanTimeToRecovery: 0,
      falsePositiveRate: 0,
      businessValue: 0,
      costEffectiveness: 0
    };
  }

  private calculateMaturityLevel(effectiveness: Partial<SOC2ControlEffectiveness>): number {
    const design = effectiveness.designEffectiveness || 0;
    const operating = effectiveness.operatingEffectiveness || 0;
    return Math.round((design + operating) / 2);
  }

  private calculateInitialEffectiveness(control: SOC2Control): Partial<SOC2ControlEffectiveness> {
    const baseEffectiveness = 60; // Starting effectiveness for newly implemented controls
    const implementationComplexity = control.implementationCost > 75000 ? 0.9 : 1.1;
    const effectiveness = baseEffectiveness * implementationComplexity;

    return {
      designEffectiveness: Math.round(effectiveness * 0.8),
      operatingEffectiveness: Math.round(effectiveness * 0.7),
      testCoverage: 50,
      exceptionRate: 15,
      meanTimeToRecovery: 4,
      falsePositiveRate: 10
    };
  }

  private simulateControlTesting(control: SOC2Control): ControlTestResult {
    const basePassRate = control.implementationCost > 50000 ? 0.8 : 0.9;
    const randomFactor = Math.random() * 0.2 - 0.1; // ±10% random variation
    const passRate = Math.max(0, Math.min(1, basePassRate + randomFactor));

    return {
      passed: passRate > 0.7,
      passRate: passRate,
      findings: this.generateTestFindings(control, passRate),
      recommendations: [],
      nextTestDate: Date.now() + (90 * 24 * 60 * 60 * 1000) // 90 days
    };
  }

  private generateTestFindings(control: SOC2Control, passRate: number): TestFinding[] {
    const findings: TestFinding[] = [];

    if (passRate < 0.8) {
      findings.push({
        severity: 'minor',
        description: 'Some evidence collection procedures need improvement',
        recommendation: 'Enhance automated evidence collection processes'
      });
    }

    if (passRate < 0.6) {
      findings.push({
        severity: 'major',
        description: 'Control effectiveness requires improvement',
        recommendation: 'Review and strengthen control implementation'
      });
    }

    return findings;
  }

  private calculateComplianceScore(controls: SOC2Control[]): number {
    if (controls.length === 0) return 0;

    const totalEffectiveness = controls.reduce((sum, control) => {
      const effectiveness = this.effectivenessData.get(control.id);
      return sum + (effectiveness?.operatingEffectiveness || 0);
    }, 0);

    return Math.round(totalEffectiveness / controls.length);
  }

  private calculateSecurityScore(controls: SOC2Control[]): number {
    if (controls.length === 0) return 0;

    const totalEffectiveness = controls.reduce((sum, control) => {
      const effectiveness = this.effectivenessData.get(control.id);
      return sum + (effectiveness?.operatingEffectiveness || 0);
    }, 0);

    // Weight security controls more heavily
    return Math.round((totalEffectiveness / controls.length) * 1.1);
  }

  private calculateBalanceRatio(complianceScore: number, securityScore: number): number {
    const total = complianceScore + securityScore;
    if (total === 0) return 0;
    return (securityScore - complianceScore) / total;
  }

  private calculateBusinessImpact(controls: SOC2Control[]): number {
    return controls.reduce((sum, control) => {
      const effectiveness = this.effectivenessData.get(control.id);
      const effectivenessMultiplier = (effectiveness?.operatingEffectiveness || 0) / 100;
      const businessImpact = Object.values(control.businessImpact).reduce((a, b) => a + b, 0);
      return sum + (businessImpact * effectivenessMultiplier);
    }, 0);
  }

  private calculateOverallRiskScore(controls: SOC2Control[]): number {
    const unmitigatedRisk = controls.reduce((sum, control) => {
      const effectiveness = this.effectivenessData.get(control.id);
      const effectivenessMultiplier = (effectiveness?.operatingEffectiveness || 0) / 100;
      const baseRisk = control.threats.reduce((riskSum, threat) =>
        riskSum + (threat.probability * threat.impact / 100), 0
      );
      const mitigatedRisk = baseRisk * (1 - effectivenessMultiplier);
      return sum + mitigatedRisk;
    }, 0);

    return Math.round(unmitigatedRisk / controls.length);
  }

  private generateBalanceRecommendations(
    balanceRatio: number,
    complianceScore: number,
    securityScore: number
  ): BalanceRecommendation[] {
    const recommendations: BalanceRecommendation[] = [];

    if (balanceRatio < -0.3) {
      recommendations.push({
        type: 'increase_security',
        controlId: 'cc6.1',
        rationale: 'Security investment significantly lagging behind compliance',
        expectedImpact: 25,
        implementationComplexity: 'medium',
        cost: 100000,
        timeframe: '3 months'
      });
    }

    if (balanceRatio > 0.3) {
      recommendations.push({
        type: 'increase_compliance',
        controlId: 'cc1.1',
        rationale: 'Compliance investment significantly lagging behind security',
        expectedImpact: 20,
        implementationComplexity: 'low',
        cost: 50000,
        timeframe: '2 months'
      });
    }

    if (complianceScore < 60) {
      recommendations.push({
        type: 'optimize_both',
        controlId: 'cc3.1',
        rationale: 'Low compliance scores require immediate attention',
        expectedImpact: 30,
        implementationComplexity: 'medium',
        cost: 75000,
        timeframe: '4 months'
      });
    }

    return recommendations;
  }

  private identifyOptimizationOpportunities(controls: SOC2Control[]): OptimizationOpportunity[] {
    const opportunities: OptimizationOpportunity[] = [];

    for (const control of controls) {
      const effectiveness = this.effectivenessData.get(control.id);
      if (effectiveness && effectiveness.costEffectiveness < 1.0) {
        opportunities.push({
          controlId: control.id,
          type: 'cost_optimization',
          description: `Optimize ${control.title} for better cost-effectiveness`,
          currentValue: effectiveness.costEffectiveness,
          targetValue: effectiveness.costEffectiveness * 1.5,
          potentialSavings: control.maintenanceCost * 0.3,
          implementationEffort: 'low'
        });
      }
    }

    return opportunities.sort((a, b) => b.potentialSavings - a.potentialSavings);
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  private calculateEffectivenessFromTests(testResult: ControlTestResult): Partial<SOC2ControlEffectiveness> {
    const testScore = testResult.passRate * 100;
    const operatingEffectiveness = Math.round(testScore * 0.9); // Test results represent operating effectiveness

    return {
      operatingEffectiveness,
      designEffectiveness: Math.round(testScore * 0.95),
      testCoverage: 85,
      exceptionRate: Math.round((1 - testResult.passRate) * 100),
      meanTimeToRecovery: 3,
      falsePositiveRate: 5
    };
  }

  private generateTestRecommendations(testResult: ControlTestResult): string[] {
    const recommendations: string[] = [];

    if (testResult.passRate < 0.8) {
      recommendations.push('Enhance control implementation and documentation');
    }

    if (testResult.findings.length > 0) {
      recommendations.push('Address test findings through remediation actions');
    }

    recommendations.push('Schedule regular testing to maintain control effectiveness');

    return recommendations;
  }
}

// Additional interfaces for the service
interface ImplementationResult {
  success: boolean;
  reason?: string;
  cost?: number;
  timeline?: number;
  expectedRiskReduction?: number;
  businessImpact?: any;
}

interface TestingResult {
  success: boolean;
  reason?: string;
  testResult: ControlTestResult;
  effectiveness: Partial<SOC2ControlEffectiveness>;
  recommendations: string[];
}

interface ControlTestResult {
  passed: boolean;
  passRate: number;
  findings: TestFinding[];
  recommendations: string[];
  nextTestDate: number;
}

interface TestFinding {
  severity: 'minor' | 'major' | 'critical';
  description: string;
  recommendation: string;
}

interface BalanceRecommendation {
  type: 'increase_security' | 'increase_compliance' | 'optimize_both';
  controlId: string;
  rationale: string;
  expectedImpact: number;
  implementationComplexity: 'low' | 'medium' | 'high';
  cost: number;
  timeframe: string;
}

interface OptimizationOpportunity {
  controlId: string;
  type: string;
  description: string;
  currentValue: number;
  targetValue: number;
  potentialSavings: number;
  implementationEffort: 'low' | 'medium' | 'high';
}

interface OptimizationPlan {
  currentBalance: number;
  targetBalance: number;
  optimizations: any[];
  totalCost: number;
  expectedBenefit: number;
  implementationPlan: any[];
  riskMitigation: number;
  timeline: number;
}

interface ScenarioConfig {
  title: string;
  description: string;
  category: string;
  difficulty: string;
  businessContext: any;
  threatLandscape: any[];
  availableControls: string[];
  targetControls: string[];
  constraints: any;
  objectives: any[];
  successMetrics: any[];
  timeLimit: number;
  costBudget: number;
  riskTolerance: number;
}

interface PlayerDecision {
  controlId: string;
  action: string;
  cost: number;
  timeSpent: number;
  rationale: string;
}

interface ScenarioResult {
  success: boolean;
  scenarioId: string;
  score: number;
  objectives: any[];
  securityComplianceBalance: number;
  learningOutcomes: string[];
  recommendations: string[];
  timeUsed: number;
  budgetUsed: number;
}

interface ControlInsights {
  overallMaturity: number;
  categoryBreakdown: any[];
  criticalControls: any[];
  riskHeatmap: any[];
  improvementOpportunities: any[];
  costOptimization: any[];
  complianceTrends: any[];
}

interface AuditReadinessPrediction {
  currentReadiness: number;
  projectedReadiness: number;
  gaps: any[];
  remediationPlan: any[];
  confidence: number;
  recommendations: string[];
}